'use client';

import cx from 'classix';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { type BlogPost } from '@/features/blog/types/blog-post-types';
import { LISTING_VIEW_VARIANTS } from '@/features/listing/constants/listing-constants';
import { type ListingCardView } from '@/features/listing/types/listing-types';
import { Link } from '@/i18n/routing';
import Button from '@/ui/button/button';
import Skeleton from '@/ui/skeleton/skeleton';

import styles from './blog-card.module.scss';

interface BlogCardProps {
  blogPost: BlogPost;
  showReadMoreButton?: boolean;
  isLoading?: boolean;
  listingsView?: ListingCardView;
}

export default function BlogCard(props: BlogCardProps) {
  const {
    blogPost,
    isLoading = false,
    listingsView = 'grid',
    showReadMoreButton = false,
  } = props;

  const translationsListingBlog = useTranslations('blog');

  return (
    <div className={cx(styles.blogCard, styles[`blogCard__${listingsView}`])}>
      {(isLoading && (
        <div className={styles.blogCard_skeleton}>
          <Skeleton />
        </div>
      ))}

      {!isLoading && (
        <Link prefetch={false} target="_blank" href={blogPost.link} className={styles.blogCard_link}>
          <div className={styles.blogCard_imageContainer}>
            <Image
              className={styles.blogCard_image}
              src={blogPost.imgUrl ?? '/images/blog/landing-offer-image-1.svg'}
              alt={blogPost.title}
              fill
            />
          </div>

          <div className={styles.blogCard_body}>
            <div className={styles.blogCard_content}>
              <div className={styles.blogCard_title}>{blogPost.title}</div>
              <div className={styles.blogCard_description}>{blogPost.description}</div>
            </div>

            {showReadMoreButton && (
              <div className={styles.blogCard_action}>
                <Button
                  element="button"
                  size={listingsView === LISTING_VIEW_VARIANTS.grid ? 'md' : 's'}
                  variant="outlined"
                  fontSize="s"
                  fullWidth
                >
                  {translationsListingBlog('read-more')}
                </Button>
              </div>
            )}
          </div>
        </Link>
      )}
    </div>
  );
}
